import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { PDFToolbar } from '../components/toolbar/PDFToolbar';
import { UploadDrawingsDialog } from '../components/upload/UploadDrawingsDialog';
import { PDFCanvasViewport } from '../components/viewer/PDFCanvasViewport';
import { ViewerPresence } from '../components/viewer/ViewerPresence';
import { DrawingsBrowser, BulkAction, BulkActionState } from '../components/browser/DrawingsBrowser';
import { usePDFDocument } from '../hooks/usePDFDocument';
import {
  useListPDFs,
  useLayerDocument,
  useListLayers,
  useLayerMutations,
  usePDFActivity,
  usePunchItems,
  useObservations,
  useDrawingSubmittals,
  useDrawingRFIs,
  usePDFRevisions,
  usePublishSheetRevision,
  useMatchPDFPageToSheet,
  useUpdatePDFPageRotation,
  usePDFPages,
  useProjectDrawingSheets,
  useUpdatePDFPageMetadata,
} from '../hooks/usePDFApi';
import { useToast } from '@/shared/hooks/use-toast';
import { deletePDF, Layer, PDFFile, PDFFileList } from '../api';
import { Button } from '@/shared/ui/button';
import { cachePdfBlobBestEffort, loadPdfBlob } from '../services/pdfBlobService';
import {
  bulkDownloadOriginals,
  bulkDownloadWithMarkup,
  BulkDownloadProgress,
} from '../services/bulkDownload';
import { useWebSocket } from '../hooks/useWebSocket';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Label } from '@/shared/ui/label';
import { loadPdfDocument } from '../services/pdfDocumentService';
import { loadPdfJs } from '../services/pdfjsLoader';
import { DRAWINGS_LIST_TOUR_ID } from '@/features/tours/drawings/list';
import { DRAWINGS_EDITOR_TOUR_ID } from '@/features/tours/drawings/editor';
import { DRAWINGS_COMBINED_TOUR_ID } from '@/features/tours/drawings/combined';
import { useTour } from '@/features/tours';

interface PDFWorkspaceProps {
  projectId: number;
}

type UndoAction =
  | { type: 'create'; layerId: string; layerDocumentId: string }
  | {
    type: 'update';
    layerId: string;
    layerDocumentId: string;
    previous: Partial<Pick<Layer, 'data' | 'markup_scope' | 'punch_list_item' | 'observation' | 'submittal' | 'rfi'>>;
  }
  | { type: 'delete'; layerDocumentId: string; layers: Layer[] };

type PdfSearchMatch = {
  pageNumber: number;
  points: Array<{ x: number; y: number }>;
  text: string;
};

export const PDFWorkspace: React.FC<PDFWorkspaceProps> = ({ projectId }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPdfId, setSelectedPdfId] = useState<string | null>(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<string[]>([]);
  const [bulkActionState, setBulkActionState] = useState<BulkActionState>({ status: 'idle' });
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  const [revisionSource, setRevisionSource] = useState<PDFFileList | null>(null);
  const [isOffline, setIsOffline] = useState(() => !navigator.onLine);
  const [activeMarkupTool, setActiveMarkupTool] = useState<'pan' | 'shape'>('pan');
  const [selectedShapeType, setSelectedShapeType] = useState<'select' | 'cloud' | 'arrow' | 'textbox' | 'pin'>('select');
  const [markupScopeFilter, setMarkupScopeFilter] = useState<'all' | 'personal' | 'shared'>('all');
  const [showActivity, setShowActivity] = useState(false);
  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkPunchItem, setLinkPunchItem] = useState<string>('');
  const [linkObservation, setLinkObservation] = useState<string>('');
  const [linkSubmittal, setLinkSubmittal] = useState<string>('');
  const [linkRfi, setLinkRfi] = useState<string>('');
  const [textDraft, setTextDraft] = useState('');
  const [fontSizeDraft, setFontSizeDraft] = useState(14);

  // Pan position lives here (not in usePDFDocument) to keep it alongside scale.
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [pendingRevisionPage, setPendingRevisionPage] = useState<number | null>(null);
  const [sheetSearch, setSheetSearch] = useState('');
  const [selectedSheetIdForMatch, setSelectedSheetIdForMatch] = useState<string>('');
  const [newSheetDrawingNumber, setNewSheetDrawingNumber] = useState('');
  const [newSheetTitle, setNewSheetTitle] = useState('');
  const [newSheetDiscipline, setNewSheetDiscipline] = useState('');
  const [manualRevisionLabel, setManualRevisionLabel] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState<PdfSearchMatch[]>([]);
  const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState(-1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchFocusRequestNonce, setSearchFocusRequestNonce] = useState<number | null>(null);
  const undoStackRef = useRef<UndoAction[]>([]);
  const isApplyingUndoRef = useRef(false);

  const {
    document,
    viewerState,
    error,
    loadDocument,
    clearDocument,
    setNumPages,
    setCurrentPage,
    setScale,
    setRotation,
  } = usePDFDocument();

  // Combine viewer state with position so the viewport has one clean object.
  const fullViewerState = { ...viewerState, position };

  // API hooks
  const { data: projectPDFs, isLoading: isLoadingPDFs, isRefetching: isRefetchingPDFs, refetch: refetchProjectPDFs } = useListPDFs(projectId);

  // ─── Guided tour auto-start ────────────────────────────────────────────────
  // Two modes share this single page:
  //   - Browser mode (``document === null``) — show the list/combined tour.
  //   - Editor mode  (``document !== null``) — show the editor tour, but
  //     only if the user didn't already see the combined walkthrough.
  // The combined tour spans both: it walks the browser, programmatically
  // opens a drawing, then continues through the editor controls.
  const {
    hydrated: tourHydrated,
    active: activeTour,
    startTour,
    hasSeen,
  } = useTour();
  const hasDrawings = (projectPDFs ?? []).length >= 1;
  useEffect(() => {
    if (!tourHydrated) return;
    if (activeTour) return;
    if (isLoadingPDFs) return;
    // Browser mode: pick combined (if items exist) or list-only.
    if (!document) {
      if (hasDrawings) {
        if (hasSeen(DRAWINGS_COMBINED_TOUR_ID)) return;
        if (hasSeen(DRAWINGS_LIST_TOUR_ID)) return;
        void startTour(DRAWINGS_COMBINED_TOUR_ID);
      } else {
        if (hasSeen(DRAWINGS_LIST_TOUR_ID)) return;
        void startTour(DRAWINGS_LIST_TOUR_ID);
      }
      return;
    }
    // Editor mode: if the user came back later and opened a drawing
    // directly without doing the combined walkthrough, walk them
    // through the editor controls now.
    if (hasSeen(DRAWINGS_COMBINED_TOUR_ID)) return;
    if (hasSeen(DRAWINGS_EDITOR_TOUR_ID)) return;
    void startTour(DRAWINGS_EDITOR_TOUR_ID);
  }, [
    tourHydrated,
    activeTour,
    startTour,
    hasSeen,
    isLoadingPDFs,
    hasDrawings,
    document,
  ]);
  // ───────────────────────────────────────────────────────────────────────────
  const { data: layerDocument } = useLayerDocument(selectedPdfId);
  const { data: pageLayers = [], refetch: refetchLayers } = useListLayers(
    layerDocument?.id ?? null,
    viewerState.currentPage,
    markupScopeFilter
  );
  const { data: revisionData } = usePDFRevisions(selectedPdfId, viewerState.currentPage);
  const publishRevisionMutation = usePublishSheetRevision(selectedPdfId);
  const matchSheetMutation = useMatchPDFPageToSheet();
  const updatePageRotationMutation = useUpdatePDFPageRotation();
  const updatePageMetadataMutation = useUpdatePDFPageMetadata(projectId);
  const { data: currentPageRows = [] } = usePDFPages(selectedPdfId, viewerState.currentPage);
  const { data: drawingSheetsData } = useProjectDrawingSheets(selectedPdfId, sheetSearch);
  const { createLayerMutation, updateLayerMutation, deleteLayerMutation } = useLayerMutations();
  const [selectedLayerIds, setSelectedLayerIds] = useState<string[]>([]);
  const [pendingDeleteLayerIds, setPendingDeleteLayerIds] = useState<string[]>([]);
  const singleSelectedLayerId = selectedLayerIds.length === 1 ? selectedLayerIds[0] : null;
  const { data: activityData, isLoading: isActivityLoading } = usePDFActivity(showActivity ? selectedPdfId : null, 100);
  const { data: punchItems = [] } = usePunchItems(projectId, linkSearch);
  const { data: allPunchItems = [] } = usePunchItems(projectId, '');
  const { data: observations = [] } = useObservations(projectId, linkSearch);
  const { data: allObservations = [] } = useObservations(projectId, '');
  const { data: submittals = [] } = useDrawingSubmittals(projectId, linkSearch);
  const { data: allSubmittals = [] } = useDrawingSubmittals(projectId, '');
  const { data: rfis = [] } = useDrawingRFIs(projectId, linkSearch);
  const { data: allRfis = [] } = useDrawingRFIs(projectId, '');
  const revisionOptions = useMemo(
    () => revisionData?.results.map((option) => ({
      id: option.id,
      label: option.label,
      pdf_file_id: option.pdf_file_id,
      pdf_page_number: option.pdf_page_number,
      sheet_revision_id: option.sheet_revision_id,
      is_current: option.is_current,
    })) ?? [],
    [revisionData?.results]
  );
  const currentPageMeta = useMemo(
    () => currentPageRows.find((row) => row.page_number === viewerState.currentPage) ?? null,
    [currentPageRows, viewerState.currentPage]
  );
  const needsSheetReview = useMemo(() => {
    if (!currentPageMeta) return false;
    const conf = currentPageMeta.sheet_match_confidence ?? null;
    return !currentPageMeta.sheet_id || (conf !== null && conf < 0.8);
  }, [currentPageMeta]);
  const punchLookup = useMemo(
    () => Object.fromEntries(allPunchItems.map((item) => [item.id, item])),
    [allPunchItems]
  );
  const observationLookup = useMemo(
    () => Object.fromEntries(allObservations.map((item) => [item.id, item])),
    [allObservations]
  );
  const submittalLookup = useMemo(
    () => Object.fromEntries(allSubmittals.map((item) => [item.id, item])),
    [allSubmittals]
  );
  const rfiLookup = useMemo(
    () => Object.fromEntries(allRfis.map((item) => [item.id, item])),
    [allRfis]
  );
  const currentPageSearchHighlights = useMemo(() => {
    if (!searchMatches.length) return [];
    return searchMatches
      .map((match, idx) => ({ ...match, idx }))
      .filter((match) => match.pageNumber === viewerState.currentPage)
      .slice(0, 300)
      .map((match) => ({
        points: match.points,
        active: match.idx === activeSearchMatchIndex,
      }));
  }, [activeSearchMatchIndex, searchMatches, viewerState.currentPage]);
  const visiblePageLayers = useMemo(
    () => pageLayers.filter((layer) => !pendingDeleteLayerIds.includes(layer.id)),
    [pageLayers, pendingDeleteLayerIds]
  );
  const {
    connect,
    disconnect,
    isConnected,
    activeUsers,
  } = useWebSocket(layerDocument?.id, {
    onLayerCreated: () => { void refetchLayers(); },
    onLayerUpdated: () => { void refetchLayers(); },
    onLayerDeleted: () => { void refetchLayers(); },
    onStateSync: () => { void refetchLayers(); },
    onError: (message) => {
      toast({
        title: 'Collaboration unavailable',
        description: message,
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!layerDocument?.id || isOffline) {
      disconnect();
      return;
    }
    connect();
  }, [layerDocument?.id, isOffline, connect, disconnect]);

  // While a drawing is open we render it as a portal over the whole viewport,
  // so prevent the underlying page from scrolling behind the viewer.
  useEffect(() => {
    if (!document) return;
    const previous = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.body.style.overflow = previous;
    };
  }, [document]);

  const handleScaleChange = useCallback(
    (nextScale: number) => {
      setScale(Math.max(0.05, Math.min(nextScale, 20)));
    },
    [setScale],
  );

  const handlePositionChange = useCallback(
    (pos: { x: number; y: number }) => {
      setPosition(pos);
    },
    [],
  );

  useEffect(() => {
    if (!currentPageMeta) return;
    const normalized = ((currentPageMeta.rotation ?? 0) % 360 + 360) % 360;
    if (normalized === viewerState.rotation) return;
    setRotation(normalized);
  }, [currentPageMeta?.id, currentPageMeta?.rotation, setRotation, viewerState.rotation]);

  const handleRotate = useCallback(() => {
    const currentRotation = ((viewerState.rotation % 360) + 360) % 360;
    const nextRotation = (currentRotation + 90) % 360;
    setRotation(nextRotation);

    if (!currentPageMeta?.id) return;
    updatePageRotationMutation.mutate(
      { pageId: currentPageMeta.id, rotation: nextRotation },
      {
        onError: (error) => {
          setRotation(currentRotation);
          toast({
            title: 'Rotation save failed',
            description: error instanceof Error ? error.message : 'Unable to save page rotation.',
            variant: 'destructive',
          });
        },
      }
    );
  }, [currentPageMeta?.id, setRotation, toast, updatePageRotationMutation, viewerState.rotation]);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage(viewerState.currentPage - 1);
  }, [setCurrentPage, viewerState.currentPage]);

  const handleNextPage = useCallback(() => {
    setCurrentPage(viewerState.currentPage + 1);
  }, [setCurrentPage, viewerState.currentPage]);

  useEffect(() => {
    if (pendingRevisionPage === null) return;
    if (!viewerState.numPages || pendingRevisionPage > viewerState.numPages) return;
    setCurrentPage(pendingRevisionPage);
    setPendingRevisionPage(null);
  }, [pendingRevisionPage, setCurrentPage, viewerState.numPages]);

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!document?.url || !trimmed) {
      setSearchMatches([]);
      setActiveSearchMatchIndex(-1);
      setIsSearchLoading(false);
      return;
    }

    let cancelled = false;
    const runSearch = async () => {
      try {
        setIsSearchLoading(true);
        const pdfjsLib = await loadPdfJs();
        const pdfDoc = await loadPdfDocument(document.url);
        const needle = trimmed.toLowerCase();
        const nextMatches: PdfSearchMatch[] = [];
        const maxMatches = 1200;

        for (let pageNumber = 1; pageNumber <= pdfDoc.numPages; pageNumber += 1) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1, rotation: 0 }) as {
            width: number;
            height: number;
            transform?: [number, number, number, number, number, number];
          };
          const textContent = await (page as any).getTextContent();
          const items = Array.isArray(textContent?.items) ? textContent.items : [];
          const viewportTransform = Array.isArray(viewport.transform)
            ? viewport.transform
            : [1, 0, 0, 1, 0, 0];
          const SUBSTRING_PAD = 0.45;
          const TOP_INSET_RATIO = 0.96;
          const BOTTOM_INSET_RATIO = 0.0;
          const BASELINE_NUDGE_RATIO = -0.03;
          let reachedMax = false;
          for (const item of items) {
            const text = String(item?.str ?? '');
            if (!text || !text.trim()) continue;
            const lower = text.toLowerCase();
            if (!lower.includes(needle)) continue;

            const itemTransform = Array.isArray(item?.transform) ? item.transform : [1, 0, 0, 10, 0, 0];
            const tx = typeof (pdfjsLib as any).Util?.transform === 'function'
              ? (pdfjsLib as any).Util.transform(viewportTransform, itemTransform)
              : itemTransform;
            const ux = Number(tx[0] ?? 1);
            const uy = Number(tx[1] ?? 0);
            const vx = Number(tx[2] ?? 0);
            const vy = Number(tx[3] ?? -1);
            const uLength = Math.hypot(ux, uy) || 1;
            const transformedHeight = Math.hypot(vx, vy);
            const reportedHeight = Math.abs(Number(item?.height ?? 0));
            const vLengthRaw = reportedHeight > 0 ? reportedHeight : transformedHeight || 10;
            const vLength = Math.max(6, Number.isFinite(vLengthRaw) ? vLengthRaw : 10);
            const uUnit = { x: ux / uLength, y: uy / uLength };
            const vUnit = transformedHeight > 0
              ? { x: vx / transformedHeight, y: vy / transformedHeight }
              : { x: -uUnit.y, y: uUnit.x };
            const baseline = {
              x: Number(tx[4] ?? 0),
              y: Number(tx[5] ?? 0),
            };
            // item.width is already in viewport text-space width for this item at scale=1.
            // Multiplying by glyph scale again inflates highlights to full-line boxes.
            const advanceRaw = Number(item?.width ?? text.length * 5);
            const advance = Math.max(1, Number.isFinite(advanceRaw) ? advanceRaw : text.length * 6);

            const charLength = Math.max(1, text.length);
            let fromIndex = 0;
            while (fromIndex < lower.length) {
              const hitIndex = lower.indexOf(needle, fromIndex);
              if (hitIndex < 0) break;
              fromIndex = hitIndex + Math.max(1, needle.length);

              const rawStart = advance * (hitIndex / charLength);
              const rawEnd = advance * ((hitIndex + needle.length) / charLength);
              const startDistance = Math.max(0, rawStart - SUBSTRING_PAD);
              const endDistance = Math.min(advance, rawEnd + SUBSTRING_PAD);
              const topInset = vLength * TOP_INSET_RATIO;
              const bottomInset = vLength * BOTTOM_INSET_RATIO;
              const baselineNudge = vLength * BASELINE_NUDGE_RATIO;
              const p0 = {
                x: baseline.x + (uUnit.x * startDistance) + (vUnit.x * (topInset + baselineNudge)),
                y: baseline.y + (uUnit.y * startDistance) + (vUnit.y * (topInset + baselineNudge)),
              };
              const p1 = {
                x: baseline.x + (uUnit.x * endDistance) + (vUnit.x * (topInset + baselineNudge)),
                y: baseline.y + (uUnit.y * endDistance) + (vUnit.y * (topInset + baselineNudge)),
              };
              const p2 = {
                x: baseline.x + (uUnit.x * endDistance) + (vUnit.x * (bottomInset + baselineNudge)),
                y: baseline.y + (uUnit.y * endDistance) + (vUnit.y * (bottomInset + baselineNudge)),
              };
              const p3 = {
                x: baseline.x + (uUnit.x * startDistance) + (vUnit.x * (bottomInset + baselineNudge)),
                y: baseline.y + (uUnit.y * startDistance) + (vUnit.y * (bottomInset + baselineNudge)),
              };

              nextMatches.push({
                pageNumber,
                points: [p0, p1, p2, p3],
                text: needle,
              });
              if (nextMatches.length >= maxMatches) {
                reachedMax = true;
                break;
              }
            }
            if (reachedMax) break;
          }
          if (reachedMax || nextMatches.length >= maxMatches) break;
        }

        if (cancelled) return;
        setSearchMatches(nextMatches);
        if (!nextMatches.length) {
          setActiveSearchMatchIndex(-1);
          return;
        }

        const samePageIndex = nextMatches.findIndex((match) => match.pageNumber === viewerState.currentPage);
        setActiveSearchMatchIndex(samePageIndex >= 0 ? samePageIndex : 0);
      } catch (searchError) {
        if (!cancelled) {
          console.error('PDF text search failed:', searchError);
          setSearchMatches([]);
          setActiveSearchMatchIndex(-1);
        }
      } finally {
        if (!cancelled) setIsSearchLoading(false);
      }
    };

    void runSearch();
    return () => {
      cancelled = true;
    };
  }, [document?.url, searchQuery, viewerState.currentPage]);

  useEffect(() => {
    if (activeSearchMatchIndex < 0 || activeSearchMatchIndex >= searchMatches.length) return;
    const activeMatch = searchMatches[activeSearchMatchIndex];
    if (!activeMatch) return;
    if (activeMatch.pageNumber !== viewerState.currentPage) {
      setCurrentPage(activeMatch.pageNumber);
    }
  }, [activeSearchMatchIndex, searchMatches, setCurrentPage, viewerState.currentPage]);

  useEffect(() => {
    if (!currentPageMeta) return;
    setNewSheetDrawingNumber(currentPageMeta.drawing_number ?? '');
    setNewSheetTitle(currentPageMeta.title ?? '');
    setNewSheetDiscipline(currentPageMeta.discipline ?? '');
    setManualRevisionLabel(currentPageMeta.sheet_revision_label ?? '');
    setSelectedSheetIdForMatch(currentPageMeta.sheet_id ?? '');
  }, [currentPageMeta?.id]);

  const handleSearchNext = useCallback(() => {
    if (!searchMatches.length) return;
    setActiveSearchMatchIndex((prev) => {
      const next = prev < 0 ? 0 : (prev + 1) % searchMatches.length;
      return next;
    });
  }, [searchMatches.length]);

  const handleSearchPrevious = useCallback(() => {
    if (!searchMatches.length) return;
    setActiveSearchMatchIndex((prev) => {
      if (prev < 0) return searchMatches.length - 1;
      return (prev - 1 + searchMatches.length) % searchMatches.length;
    });
  }, [searchMatches.length]);

  const handleSearchEnterNext = useCallback(() => {
    if (!searchMatches.length) return;
    setActiveSearchMatchIndex((prev) => {
      const next = prev < 0 ? 0 : (prev + 1) % searchMatches.length;
      setSearchFocusRequestNonce((current) => (current ?? 0) + 1);
      return next;
    });
  }, [searchMatches.length]);

  const handleSearchEnterPrevious = useCallback(() => {
    if (!searchMatches.length) return;
    setActiveSearchMatchIndex((prev) => {
      const next = prev < 0 ? searchMatches.length - 1 : (prev - 1 + searchMatches.length) % searchMatches.length;
      setSearchFocusRequestNonce((current) => (current ?? 0) + 1);
      return next;
    });
  }, [searchMatches.length]);

  const handleDownload = useCallback(() => {
    if (!document) return;
    const link = window.document.createElement('a');
    link.href = document.url;
    link.download = document.name;
    link.click();
  }, [document]);

  const resetViewState = useCallback(() => {
    setPosition({ x: 0, y: 0 });
  }, []);

  const handlePublishRevision = useCallback(async (sheetRevisionId?: string) => {
    if (!sheetRevisionId || !selectedPdfId) return;
    try {
      await publishRevisionMutation.mutateAsync(sheetRevisionId);
      toast({
        title: 'Revision published',
        description: 'This revision is now current for the sheet.',
        className: 'bg-green-500 text-white',
      });
    } catch (error) {
      toast({
        title: 'Publish failed',
        description: error instanceof Error ? error.message : 'Unable to publish revision.',
        variant: 'destructive',
      });
    }
  }, [publishRevisionMutation, selectedPdfId, toast]);

  const handleManualSheetMatch = useCallback(async (mode: 'existing' | 'new') => {
    if (!currentPageMeta?.id) return;
    try {
      if (mode === 'existing' && selectedSheetIdForMatch) {
        await matchSheetMutation.mutateAsync({
          pageId: currentPageMeta.id,
          payload: {
            sheet_id: selectedSheetIdForMatch,
            revision_label: manualRevisionLabel || undefined,
            set_current: true,
          },
        });
      } else if (mode === 'new') {
        await matchSheetMutation.mutateAsync({
          pageId: currentPageMeta.id,
          payload: {
            create_new_sheet: true,
            drawing_number: newSheetDrawingNumber,
            title: newSheetTitle,
            discipline: newSheetDiscipline,
            revision_label: manualRevisionLabel || undefined,
            set_current: true,
          },
        });
      } else {
        return;
      }
      toast({
        title: 'Sheet match saved',
        description: 'Page sheet identity was updated successfully.',
        className: 'bg-green-500 text-white',
      });
    } catch (error) {
      toast({
        title: 'Sheet match failed',
        description: error instanceof Error ? error.message : 'Unable to update sheet identity.',
        variant: 'destructive',
      });
    }
  }, [
    currentPageMeta?.id,
    matchSheetMutation,
    manualRevisionLabel,
    newSheetDiscipline,
    newSheetDrawingNumber,
    newSheetTitle,
    selectedSheetIdForMatch,
    toast,
  ]);

  const handleUploadSuccess = useCallback(async (pdfFile: PDFFile, sourceFile: File) => {
    await cachePdfBlobBestEffort(pdfFile.id, sourceFile.name, sourceFile);
    resetViewState();
    loadDocument(sourceFile);
    setSelectedPdfId(pdfFile.id);
    setUploadDialogOpen(false);
  }, [loadDocument, resetViewState]);

  const handleBulkAction = useCallback(async (action: BulkAction, pdfs: PDFFileList[]) => {
    if (!pdfs.length) return;
    if (bulkActionState.status === 'running') return;

    const total = pdfs.length;
    const reportDownloadProgress = (next: BulkDownloadProgress) => {
      setBulkActionState({
        status: 'running',
        action,
        total: next.total,
        completed: next.completed,
        currentFilename: next.currentFilename,
      });
    };

    setBulkActionState({ status: 'running', action, total, completed: 0, currentFilename: pdfs[0]?.original_filename ?? null });

    try {
      if (action === 'download-original') {
        const { succeeded, failed } = await bulkDownloadOriginals(pdfs, reportDownloadProgress);
        toast({
          title: failed.length ? 'Some downloads failed' : 'Downloads ready',
          description: failed.length
            ? `${succeeded} of ${total} drawings downloaded. ${failed.length} failed.`
            : `${succeeded} drawing${succeeded === 1 ? '' : 's'} downloaded.`,
          variant: failed.length ? 'destructive' : undefined,
        });
        setBulkActionState({ status: 'success', action, succeeded, failed: failed.length });
        return;
      }

      if (action === 'download-markup') {
        const { succeeded, failed } = await bulkDownloadWithMarkup(pdfs, reportDownloadProgress);
        toast({
          title: failed.length ? 'Some markup exports failed' : 'Markup-flattened PDFs ready',
          description: failed.length
            ? `${succeeded} of ${total} drawings exported with markup. ${failed.length} failed.`
            : `${succeeded} drawing${succeeded === 1 ? '' : 's'} exported with markup.`,
          variant: failed.length ? 'destructive' : undefined,
        });
        setBulkActionState({ status: 'success', action, succeeded, failed: failed.length });
        return;
      }

      if (action === 'delete') {
        const proceed = window.confirm(
          `Delete ${total} drawing${total === 1 ? '' : 's'}? This cannot be undone.`
        );
        if (!proceed) {
          setBulkActionState({ status: 'idle' });
          return;
        }

        let completed = 0;
        const failed: string[] = [];
        for (const pdf of pdfs) {
          setBulkActionState({ status: 'running', action, total, completed, currentFilename: pdf.original_filename });
          try {
            await deletePDF(pdf.id);
            completed += 1;
          } catch (error) {
            console.error('Failed to delete drawing', pdf.id, error);
            failed.push(pdf.original_filename);
          }
        }

        if (selectedPdfId && pdfs.some((pdf) => pdf.id === selectedPdfId)) {
          setSelectedPdfId(null);
          clearDocument();
        }
        setBulkSelectedIds([]);
        await refetchProjectPDFs();
        toast({
          title: failed.length ? 'Some deletions failed' : 'Drawings deleted',
          description: failed.length
            ? `Deleted ${completed} drawing${completed === 1 ? '' : 's'}. Failed: ${failed.join(', ')}`
            : `${completed} drawing${completed === 1 ? '' : 's'} deleted.`,
          variant: failed.length ? 'destructive' : undefined,
        });
        setBulkActionState({ status: 'success', action, succeeded: completed, failed: failed.length });
      }
    } catch (error) {
      console.error('Bulk action failed', error);
      toast({
        title: 'Bulk action failed',
        description: error instanceof Error ? error.message : 'Unable to complete bulk action.',
        variant: 'destructive',
      });
      setBulkActionState({
        status: 'error',
        action,
        message: error instanceof Error ? error.message : 'Unable to complete bulk action.',
      });
    }
  }, [bulkActionState.status, clearDocument, refetchProjectPDFs, selectedPdfId, toast]);

  // Auto reset bulk state after a short delay once it succeeds, so the menu reverts.
  useEffect(() => {
    if (bulkActionState.status !== 'success') return;
    const timer = setTimeout(() => setBulkActionState({ status: 'idle' }), 4000);
    return () => clearTimeout(timer);
  }, [bulkActionState]);

  const handleUploadError = useCallback((error: Error) => {
    console.error('Upload error:', error);
    toast({
      title: 'Upload Failed',
      description: error.message || 'Failed to upload PDF',
      variant: 'destructive',
    });
  }, [toast]);

  // Handle PDF selection from list
  const handlePDFSelect = useCallback(async (pdf: PDFFileList) => {
    setSelectedPdfId(pdf.id);
    resetViewState();

    try {
      const blob = await loadPdfBlob({
        pdfId: pdf.id,
        fileName: pdf.original_filename,
        fileUrl: pdf.file_url,
      });

      const file = new File([blob], pdf.original_filename, { type: 'application/pdf' });
      loadDocument(file);
    } catch (err) {
      console.error('Error loading PDF:', err);
      toast({
        title: 'Error',
        description: 'Failed to load PDF (not available online or in offline cache)',
        variant: 'destructive',
      });
    }
  }, [loadDocument, toast, resetViewState]);

  const handleHyperlinkNavigate = useCallback(async (target: { pdfFileId: string; pageNumber: number; referenceToken?: string }) => {
    const targetPdf = (projectPDFs ?? []).find((pdf) => pdf.id === target.pdfFileId);
    if (!targetPdf) {
      toast({
        title: 'Linked drawing unavailable',
        description: target.referenceToken
          ? `Unable to find linked drawing ${target.referenceToken}.`
          : 'Unable to find linked drawing.',
        variant: 'destructive',
      });
      return;
    }

    const nextPage = Math.max(1, Number(target.pageNumber) || 1);
    if (selectedPdfId === targetPdf.id) {
      setCurrentPage(nextPage);
      return;
    }

    setPendingRevisionPage(nextPage);
    await handlePDFSelect(targetPdf);
  }, [handlePDFSelect, projectPDFs, selectedPdfId, setCurrentPage, toast]);

  useEffect(() => {
    setSelectedLayerIds((prev) => {
      const next = prev.filter((id) => visiblePageLayers.some((layer) => layer.id === id));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [visiblePageLayers]);

  const selectedLayer = singleSelectedLayerId
    ? visiblePageLayers.find((layer) => layer.id === singleSelectedLayerId) ?? null
    : null;
  const isTextLayerSelected = selectedLayer?.layer_type === 'text' || selectedLayer?.layer_type === 'annotation';

  const pushUndoAction = useCallback((action: UndoAction) => {
    if (isApplyingUndoRef.current) return;
    undoStackRef.current.push(action);
    if (undoStackRef.current.length > 100) {
      undoStackRef.current.shift();
    }
  }, []);

  useEffect(() => {
    if (!selectedLayer) {
      setShowLinkPanel(false);
      setLinkPunchItem('');
      setLinkObservation('');
      setLinkSubmittal('');
      setLinkRfi('');
      setTextDraft('');
      setFontSizeDraft(14);
      return;
    }
    setLinkPunchItem(selectedLayer.punch_list_item ?? '');
    setLinkObservation(selectedLayer.observation ?? '');
    setLinkSubmittal(selectedLayer.submittal ?? '');
    setLinkRfi(selectedLayer.rfi ?? '');
    if (selectedLayer.layer_type === 'text' || selectedLayer.layer_type === 'annotation') {
      const layerText = typeof selectedLayer.data?.text === 'string' ? selectedLayer.data.text : '';
      const layerFontSize = Number(selectedLayer.data?.fontSize ?? 14);
      setTextDraft(layerText);
      setFontSizeDraft(Math.max(8, Math.min(96, Number.isFinite(layerFontSize) ? layerFontSize : 14)));
    }
  }, [selectedLayer]);

  const handleShapeDrawComplete = useCallback(async (
    start: { x: number; y: number },
    end: { x: number; y: number }
  ) => {
    if (!layerDocument || activeMarkupTool !== 'shape' || selectedShapeType === 'select') return;
    const zIndex = (pageLayers.length ? pageLayers[pageLayers.length - 1].z_index : 0) + 1;

    const base = {
      layer_document: layerDocument.id,
      page_number: viewerState.currentPage,
      visible: true,
      z_index: zIndex,
      markup_scope: 'shared' as const,
      punch_list_item: null,
      observation: null,
      submittal: null,
      rfi: null,
    } as const;

    if (selectedShapeType === 'textbox') {
      const x = Math.min(start.x, end.x);
      const y = Math.min(start.y, end.y);
      const width = Math.max(60, Math.abs(end.x - start.x));
      const height = Math.max(28, Math.abs(end.y - start.y));
      const created = await createLayerMutation.mutateAsync({
        ...base,
        layer_type: 'text',
        data: {
          x: Math.max(8, Math.round(x)),
          y: Math.max(16, Math.round(y)),
          width: Math.round(width),
          height: Math.round(height),
          text: '',
          fontSize: 14,
        },
      });
      if (created?.id) {
        pushUndoAction({ type: 'create', layerId: created.id, layerDocumentId: layerDocument.id });
        setSelectedLayerIds([created.id]);
      }
    } else if (selectedShapeType === 'cloud') {
      const x = Math.min(start.x, end.x);
      const y = Math.min(start.y, end.y);
      const width = Math.max(24, Math.abs(end.x - start.x));
      const height = Math.max(24, Math.abs(end.y - start.y));
      const created = await createLayerMutation.mutateAsync({
        ...base,
        layer_type: 'shape',
        data: {
          shapeType: 'cloud',
          x: Math.round(x),
          y: Math.round(y),
          width: Math.round(width),
          height: Math.round(height),
        },
      });
      if (created?.id) {
        pushUndoAction({ type: 'create', layerId: created.id, layerDocumentId: layerDocument.id });
      }
    } else if (selectedShapeType === 'arrow') {
      const created = await createLayerMutation.mutateAsync({
        ...base,
        layer_type: 'shape',
        data: {
          shapeType: 'arrow',
          x1: Math.round(start.x),
          y1: Math.round(start.y),
          x2: Math.round(end.x),
          y2: Math.round(end.y),
        },
      });
      if (created?.id) {
        pushUndoAction({ type: 'create', layerId: created.id, layerDocumentId: layerDocument.id });
      }
    } else if (selectedShapeType === 'pin') {
      const created = await createLayerMutation.mutateAsync({
        ...base,
        layer_type: 'shape',
        data: {
          shapeType: 'pin',
          x: Math.round(start.x - 12),
          y: Math.round(start.y - 24),
          width: 24,
          height: 24,
        },
      });
      if (created?.id) {
        pushUndoAction({ type: 'create', layerId: created.id, layerDocumentId: layerDocument.id });
      }
    }
    void refetchLayers();
  }, [
    activeMarkupTool,
    createLayerMutation,
    layerDocument,
    pageLayers,
    refetchLayers,
    selectedShapeType,
    pushUndoAction,
    viewerState.currentPage,
  ]);

  const handleSaveLink = useCallback(async () => {
    if (!singleSelectedLayerId || !layerDocument?.id) return;
    const currentLayer = pageLayers.find((layer) => layer.id === singleSelectedLayerId);
    if (currentLayer) {
      pushUndoAction({
        type: 'update',
        layerId: currentLayer.id,
        layerDocumentId: layerDocument.id,
        previous: {
          data: currentLayer.data,
          markup_scope: currentLayer.markup_scope,
          punch_list_item: currentLayer.punch_list_item,
          observation: currentLayer.observation,
          submittal: currentLayer.submittal,
          rfi: currentLayer.rfi,
        },
      });
    }
    const selectedObservationItem = allObservations.find((item) => item.id === linkObservation);
    const selectedPunchItem = allPunchItems.find((item) => item.id === linkPunchItem);
    const selectedSubmittalItem = allSubmittals.find((item) => item.id === linkSubmittal);
    const selectedRfiItem = allRfis.find((item) => item.id === linkRfi);
    const nextData: Record<string, unknown> = { ...(currentLayer?.data ?? {}) };
    if (linkObservation) {
      nextData.observation_label = selectedObservationItem?.number !== undefined
        ? `OBS-${selectedObservationItem.number}`
        : String(nextData.observation_label ?? 'OBS');
    } else if ('observation_label' in nextData) {
      delete nextData.observation_label;
    }
    if (linkPunchItem) {
      nextData.punch_label = selectedPunchItem?.title
        ? `PUNCH: ${selectedPunchItem.title}`
        : String(nextData.punch_label ?? 'PUNCH');
    } else if ('punch_label' in nextData) {
      delete nextData.punch_label;
    }
    if (linkSubmittal) {
      nextData.submittal_label = selectedSubmittalItem?.submittal_number
        ? selectedSubmittalItem.submittal_number
        : String(nextData.submittal_label ?? 'SUBMITTAL');
    } else if ('submittal_label' in nextData) {
      delete nextData.submittal_label;
    }
    if (linkRfi) {
      nextData.rfi_label = selectedRfiItem?.rfi_number
        ? selectedRfiItem.rfi_number
        : String(nextData.rfi_label ?? 'RFI');
    } else if ('rfi_label' in nextData) {
      delete nextData.rfi_label;
    }
    await updateLayerMutation.mutateAsync({
      layerId: singleSelectedLayerId,
      layerDocumentId: layerDocument.id,
      updates: {
        data: nextData,
        punch_list_item: linkPunchItem || null,
        observation: linkObservation || null,
        submittal: linkSubmittal || null,
        rfi: linkRfi || null,
      },
    });
    setShowLinkPanel(false);
    void refetchLayers();
  }, [
    layerDocument?.id,
    linkObservation,
    linkPunchItem,
    linkSubmittal,
    linkRfi,
    allObservations,
    allPunchItems,
    allSubmittals,
    allRfis,
    pageLayers,
    pushUndoAction,
    refetchLayers,
    singleSelectedLayerId,
    updateLayerMutation,
  ]);

  const handleLayerSelect = useCallback((
    layerId: string,
    options?: { toggle?: boolean; exclusive?: boolean }
  ) => {
    if (options?.toggle) {
      setSelectedLayerIds((prev) => (
        prev.includes(layerId)
          ? prev.filter((id) => id !== layerId)
          : [...prev, layerId]
      ));
      return;
    }

    if (options?.exclusive || !options) {
      setSelectedLayerIds([layerId]);
    }
  }, []);

  const handleLayerDataChange = useCallback(async (layerId: string, nextData: Record<string, unknown>) => {
    if (!layerDocument?.id) return;
    const currentLayer = pageLayers.find((layer) => layer.id === layerId);
    if (currentLayer) {
      pushUndoAction({
        type: 'update',
        layerId,
        layerDocumentId: layerDocument.id,
        previous: {
          data: currentLayer.data,
        },
      });
    }
    await updateLayerMutation.mutateAsync({
      layerId,
      layerDocumentId: layerDocument.id,
      updates: {
        data: nextData,
      },
    });
    void refetchLayers();
  }, [layerDocument?.id, pageLayers, pushUndoAction, refetchLayers, updateLayerMutation]);

  const applyUndo = useCallback(async () => {
    const action = undoStackRef.current.pop();
    if (!action) return;
    isApplyingUndoRef.current = true;
    try {
      if (action.type === 'create') {
        await deleteLayerMutation.mutateAsync({
          layerId: action.layerId,
          layerDocumentId: action.layerDocumentId,
        });
        setSelectedLayerIds((prev) => prev.filter((id) => id !== action.layerId));
      } else if (action.type === 'update') {
        await updateLayerMutation.mutateAsync({
          layerId: action.layerId,
          layerDocumentId: action.layerDocumentId,
          updates: action.previous,
        });
      } else {
        for (const layer of action.layers) {
          await createLayerMutation.mutateAsync({
            layer_document: action.layerDocumentId,
            page_number: layer.page_number,
            layer_type: layer.layer_type,
            data: layer.data,
            z_index: layer.z_index,
            visible: layer.visible,
            markup_scope: layer.markup_scope,
            punch_list_item: layer.punch_list_item,
            observation: layer.observation,
            submittal: layer.submittal,
            rfi: layer.rfi,
          });
        }
      }
      void refetchLayers();
    } finally {
      isApplyingUndoRef.current = false;
    }
  }, [createLayerMutation, deleteLayerMutation, refetchLayers, updateLayerMutation]);

  const handleSaveTextProperties = useCallback(async () => {
    if (!selectedLayer || !layerDocument?.id || !isTextLayerSelected) return;
    const currentData = selectedLayer.data ?? {};
    const nextFontSize = Math.max(8, Math.min(96, Math.round(fontSizeDraft)));
    await handleLayerDataChange(selectedLayer.id, {
      ...currentData,
      text: textDraft,
      fontSize: nextFontSize,
    });
  }, [fontSizeDraft, handleLayerDataChange, isTextLayerSelected, layerDocument?.id, selectedLayer, textDraft]);

  const commitSelectedTextIfDirty = useCallback(async () => {
    if (!selectedLayer || !isTextLayerSelected) return;
    const persistedText = typeof selectedLayer.data?.text === 'string' ? selectedLayer.data.text : '';
    const persistedFontSize = Math.max(8, Math.min(96, Number(selectedLayer.data?.fontSize ?? 14)));
    const nextFontSize = Math.max(8, Math.min(96, Math.round(fontSizeDraft)));
    if (persistedText === textDraft && persistedFontSize === nextFontSize) {
      return;
    }
    await handleSaveTextProperties();
  }, [fontSizeDraft, handleSaveTextProperties, isTextLayerSelected, selectedLayer, textDraft]);

  const handleCanvasBackgroundClick = useCallback(() => {
    void (async () => {
      await commitSelectedTextIfDirty();
      setSelectedLayerIds([]);
      setShowLinkPanel(false);
    })();
  }, [commitSelectedTextIfDirty]);

  const handleDeleteSelectedLayers = useCallback(async () => {
    if (!selectedLayerIds.length || !layerDocument?.id) return;
    const deleteIds = [...selectedLayerIds];
    const deletedLayers = pageLayers.filter((layer) => deleteIds.includes(layer.id));
    if (!deletedLayers.length) return;
    setPendingDeleteLayerIds((prev) => Array.from(new Set([...prev, ...deleteIds])));
    setSelectedLayerIds([]);
    setShowLinkPanel(false);
    try {
      await Promise.all(
        deleteIds.map((layerId) => deleteLayerMutation.mutateAsync({
          layerId,
          layerDocumentId: layerDocument.id,
        }))
      );
      pushUndoAction({
        type: 'delete',
        layerDocumentId: layerDocument.id,
        layers: deletedLayers,
      });
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Unable to delete selected markup.',
        variant: 'destructive',
      });
    } finally {
      setPendingDeleteLayerIds((prev) => prev.filter((id) => !deleteIds.includes(id)));
      void refetchLayers();
    }
  }, [deleteLayerMutation, layerDocument?.id, pageLayers, pushUndoAction, refetchLayers, selectedLayerIds, toast]);

  const handleOpenObservationDetail = useCallback((observationId: string) => {
    const basePath = location.pathname.replace(/\/(asbuilds|progress|drawings)(?:\/.*)?$/, '');
    const fallbackBasePath = location.pathname.replace(/\/pdf(?:-editor)?(?:\/.*)?$/, '');
    const resolvedBase = (basePath !== location.pathname ? basePath : fallbackBasePath);
    navigate(`${resolvedBase}/observations/${observationId}`);
  }, [location.pathname, navigate]);

  const handleOpenPunchDetail = useCallback((itemId: string) => {
    const basePath = location.pathname.replace(/\/(asbuilds|progress|drawings)(?:\/.*)?$/, '');
    const fallbackBasePath = location.pathname.replace(/\/pdf(?:-editor)?(?:\/.*)?$/, '');
    const resolvedBase = (basePath !== location.pathname ? basePath : fallbackBasePath);
    navigate(`${resolvedBase}/punch-lists/${itemId}`);
  }, [location.pathname, navigate]);

  const handleOpenSubmittalDetail = useCallback((submittalId: string) => {
    const basePath = location.pathname.replace(/\/(asbuilds|progress|drawings)(?:\/.*)?$/, '');
    const fallbackBasePath = location.pathname.replace(/\/pdf(?:-editor)?(?:\/.*)?$/, '');
    const resolvedBase = (basePath !== location.pathname ? basePath : fallbackBasePath);
    navigate(`${resolvedBase}/submittals/${submittalId}`);
  }, [location.pathname, navigate]);

  const handleOpenRfiDetail = useCallback((rfiId: string) => {
    const basePath = location.pathname.replace(/\/(asbuilds|progress|drawings)(?:\/.*)?$/, '');
    const fallbackBasePath = location.pathname.replace(/\/pdf(?:-editor)?(?:\/.*)?$/, '');
    const resolvedBase = (basePath !== location.pathname ? basePath : fallbackBasePath);
    navigate(`${resolvedBase}/rfis/${rfiId}`);
  }, [location.pathname, navigate]);

  const handleDuplicateSelectedLayer = useCallback(async () => {
    if (!selectedLayer || !layerDocument?.id) return;
    const sourceData = selectedLayer.data ?? {};
    const duplicatedData: Record<string, unknown> = { ...sourceData };
    if (
      typeof sourceData.x1 === 'number' &&
      typeof sourceData.y1 === 'number' &&
      typeof sourceData.x2 === 'number' &&
      typeof sourceData.y2 === 'number'
    ) {
      duplicatedData.x1 = sourceData.x1 + 16;
      duplicatedData.y1 = sourceData.y1 + 16;
      duplicatedData.x2 = sourceData.x2 + 16;
      duplicatedData.y2 = sourceData.y2 + 16;
    } else {
      duplicatedData.x = Number(sourceData.x ?? 0) + 16;
      duplicatedData.y = Number(sourceData.y ?? 0) + 16;
    }

    const created = await createLayerMutation.mutateAsync({
      layer_document: layerDocument.id,
      page_number: selectedLayer.page_number,
      layer_type: selectedLayer.layer_type,
      data: duplicatedData,
      z_index: selectedLayer.z_index + 1,
      visible: selectedLayer.visible,
      markup_scope: selectedLayer.markup_scope,
      punch_list_item: selectedLayer.punch_list_item,
      observation: selectedLayer.observation,
      submittal: selectedLayer.submittal,
      rfi: selectedLayer.rfi,
    });
    if (created?.id) {
      pushUndoAction({ type: 'create', layerId: created.id, layerDocumentId: layerDocument.id });
      setSelectedLayerIds([created.id]);
    }
    void refetchLayers();
  }, [createLayerMutation, layerDocument?.id, pushUndoAction, refetchLayers, selectedLayer]);

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const isUndoShortcut = (event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === 'z';
      if (isUndoShortcut) {
        event.preventDefault();
        await applyUndo();
        return;
      }

      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      event.preventDefault();
      await handleDeleteSelectedLayers();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [applyUndo, handleDeleteSelectedLayers]);

  const handleOpenUpload = useCallback(() => {
    setPendingUploadFiles([]);
    setRevisionSource(null);
    setUploadDialogOpen(true);
  }, []);

  const handleDroppedFiles = useCallback((files: File[]) => {
    setPendingUploadFiles(files);
    setRevisionSource(null);
    setUploadDialogOpen(true);
  }, []);

  const handleUploadRevision = useCallback((pdf: PDFFileList) => {
    setPendingUploadFiles([]);
    setRevisionSource(pdf);
    setUploadDialogOpen(true);
  }, []);

  const handleUpdateDrawingMetadata = useCallback(async (
    pdf: PDFFileList,
    pageId: string,
    updates: { drawing_number?: string; title?: string; discipline?: string }
  ) => {
    try {
      await updatePageMetadataMutation.mutateAsync({ pdfId: pdf.id, pageId, updates });
      toast({
        title: 'Drawing updated',
        description: 'The drawing metadata was saved.',
      });
    } catch (error) {
      toast({
        title: 'Could not update drawing',
        description: error instanceof Error ? error.message : 'The drawing metadata could not be saved.',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast, updatePageMetadataMutation]);

  const uploadDialog = (
    <UploadDrawingsDialog
      open={uploadDialogOpen}
      onOpenChange={(open) => {
        setUploadDialogOpen(open);
        if (!open) {
          setPendingUploadFiles([]);
          setRevisionSource(null);
        }
      }}
      projectId={projectId}
      initialFiles={pendingUploadFiles}
      revisionSource={revisionSource}
      onSingleUploadSuccess={(pdfFile, sourceFile) => { void handleUploadSuccess(pdfFile, sourceFile); }}
      onSingleUploadError={handleUploadError}
      onBatchComplete={async () => { await refetchProjectPDFs(); }}
    />
  );

  if (!document) {
    return (
      <div className="flex-1 flex overflow-hidden">
        <DrawingsBrowser
          pdfs={projectPDFs ?? []}
          isLoading={isLoadingPDFs}
          isRefetching={isRefetchingPDFs}
          onRefresh={() => { void refetchProjectPDFs(); }}
          onOpen={(pdf) => { void handlePDFSelect(pdf); }}
          onUploadClick={handleOpenUpload}
          onUploadRevision={handleUploadRevision}
          onFilesDropped={handleDroppedFiles}
          onUpdateMetadata={handleUpdateDrawingMetadata}
          selectedIds={bulkSelectedIds}
          onSelectedIdsChange={setBulkSelectedIds}
          onBulkAction={(action, pdfs) => { void handleBulkAction(action, pdfs); }}
          bulkActionState={bulkActionState}
        />
        {error && (
          <div className="absolute bottom-4 right-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md max-w-md">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}
        {uploadDialog}
      </div>
    );
  }

  return createPortal(
    // Keep the drawing viewer above the app shell, including the desktop
    // sidebar (z-[1200]), while leaving Radix popovers/dropdowns (z-[2100])
    // available for toolbar menus and presence details.
    <div className="fixed inset-0 z-[1300] flex flex-col bg-background">
      <PDFToolbar
        viewerState={fullViewerState}
        fileName={document.name}
        fileOptions={(projectPDFs ?? []).map((pdf) => ({
          id: pdf.id,
          label: pdf.original_filename,
        }))}
        selectedFileId={selectedPdfId}
        onFileSelect={(option) => {
          const pdf = projectPDFs?.find((item) => item.id === option.id);
          if (pdf) {
            void handlePDFSelect(pdf);
          }
        }}
        onRotate={handleRotate}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onClose={clearDocument}
        onDownload={handleDownload}
        revisionOptions={revisionOptions}
        selectedRevisionId={revisionData?.results.find((option) => option.is_selected)?.id ?? selectedPdfId}
        onRevisionSelect={(option) => {
          const targetPdfId = option.pdf_file_id ?? option.id;
          const targetPage = option.pdf_page_number ?? 1;
          if (targetPdfId === selectedPdfId) {
            setCurrentPage(targetPage);
            return;
          }
          const pdf = projectPDFs?.find((item) => item.id === targetPdfId);
          if (pdf) {
            setPendingRevisionPage(targetPage);
            void handlePDFSelect(pdf);
          }
        }}
        onPublishRevision={(option) => {
          void handlePublishRevision(option.sheet_revision_id);
        }}
        activeMarkupTool={activeMarkupTool}
        onMarkupToolChange={setActiveMarkupTool}
        selectedShapeType={selectedShapeType}
        onSelectedShapeTypeChange={setSelectedShapeType}
        markupScopeFilter={markupScopeFilter}
        onMarkupScopeFilterChange={setMarkupScopeFilter}
        onToggleActivity={() => setShowActivity((prev) => !prev)}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        onSearchNext={handleSearchNext}
        onSearchPrevious={handleSearchPrevious}
        onSearchEnterNext={handleSearchEnterNext}
        onSearchEnterPrevious={handleSearchEnterPrevious}
        searchResultCount={searchMatches.length}
        activeSearchResultIndex={activeSearchMatchIndex >= 0 ? activeSearchMatchIndex + 1 : 0}
        isSearchLoading={isSearchLoading}
      />
      <div className="flex-1 overflow-hidden relative">
        <div className="absolute z-10 right-4 top-3">
          <ViewerPresence
            activeUsers={activeUsers}
            isConnected={isConnected}
            isOffline={isOffline}
          />
        </div>
        <div className="pointer-events-none absolute z-10 bottom-3 right-4 rounded-md bg-background/95 border px-2.5 py-1 text-xs text-muted-foreground">
          {`${Math.round(fullViewerState.scale * 100)}%`}
        </div>
        {currentPageMeta && needsSheetReview && (
          <Card className="absolute left-4 top-14 z-20 w-80">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">
                Sheet Review Needed
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="text-xs text-muted-foreground">
                {`Page ${currentPageMeta.page_number} confidence: ${
                  typeof currentPageMeta.sheet_match_confidence === 'number'
                    ? `${Math.round(currentPageMeta.sheet_match_confidence * 100)}%`
                    : 'unknown'
                }`}
              </div>
              <Input
                className="h-8 text-xs"
                value={sheetSearch}
                onChange={(e) => setSheetSearch(e.target.value)}
                placeholder="Search existing sheets"
              />
              <Select
                value={selectedSheetIdForMatch || '__none__'}
                onValueChange={(value) => setSelectedSheetIdForMatch(value === '__none__' ? '' : value)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select existing sheet (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No existing sheet selected</SelectItem>
                  {(drawingSheetsData?.results ?? []).map((sheet) => (
                    <SelectItem key={sheet.id} value={sheet.id}>
                      {sheet.drawing_number
                        ? `${sheet.drawing_number} - ${sheet.title || 'Untitled'}`
                        : sheet.title || sheet.identity_key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="h-8 text-xs"
                value={newSheetDrawingNumber}
                onChange={(e) => setNewSheetDrawingNumber(e.target.value)}
                placeholder="New sheet drawing number"
              />
              <Input
                className="h-8 text-xs"
                value={newSheetTitle}
                onChange={(e) => setNewSheetTitle(e.target.value)}
                placeholder="New sheet title"
              />
              <Input
                className="h-8 text-xs"
                value={newSheetDiscipline}
                onChange={(e) => setNewSheetDiscipline(e.target.value)}
                placeholder="New sheet discipline"
              />
              <Input
                className="h-8 text-xs"
                value={manualRevisionLabel}
                onChange={(e) => setManualRevisionLabel(e.target.value)}
                placeholder="Revision label (optional)"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!selectedSheetIdForMatch || matchSheetMutation.isPending}
                  onClick={() => { void handleManualSheetMatch('existing'); }}
                >
                  Match Existing
                </Button>
                <Button
                  size="sm"
                  disabled={matchSheetMutation.isPending}
                  onClick={() => { void handleManualSheetMatch('new'); }}
                >
                  Create New
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <PDFCanvasViewport
          fileUrl={document.url}
          viewerState={fullViewerState}
          onLoadSuccess={setNumPages}
          onScaleChange={handleScaleChange}
          onPositionChange={handlePositionChange}
          drawingEnabled={activeMarkupTool === 'shape' && selectedShapeType !== 'select'}
          drawingShapeType={selectedShapeType === 'select' ? 'cloud' : selectedShapeType}
          onShapeDrawComplete={handleShapeDrawComplete}
          layers={visiblePageLayers}
          selectedLayerIds={selectedLayerIds}
          onLayerClick={handleLayerSelect}
          onBackgroundClick={handleCanvasBackgroundClick}
          onLayerChange={handleLayerDataChange}
          onSelectedLayerLink={() => setShowLinkPanel(true)}
          onSelectedLayerDelete={() => { void handleDeleteSelectedLayers(); }}
          onSelectedLayerDuplicate={() => { void handleDuplicateSelectedLayer(); }}
          selectedTextDraft={isTextLayerSelected ? textDraft : undefined}
          selectedTextFontSize={isTextLayerSelected ? fontSizeDraft : undefined}
          onSelectedTextDraftChange={setTextDraft}
          onSelectedTextFontSizeChange={setFontSizeDraft}
          onSelectedTextCommit={() => { void handleSaveTextProperties(); }}
          punchLookup={punchLookup}
          observationLookup={observationLookup}
          submittalLookup={submittalLookup}
          rfiLookup={rfiLookup}
          onPunchLabelClick={handleOpenPunchDetail}
          onObservationLabelClick={handleOpenObservationDetail}
          onSubmittalLabelClick={handleOpenSubmittalDetail}
          onRfiLabelClick={handleOpenRfiDetail}
          onHyperlinkNavigate={(target) => { void handleHyperlinkNavigate(target); }}
          searchHighlights={currentPageSearchHighlights}
          searchFocusRequestNonce={searchFocusRequestNonce ?? undefined}
        />
        {selectedLayer && showLinkPanel && (
          <Card className="absolute left-4 top-44 z-20 w-72">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">
                Link Item
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input
                className="h-8 text-xs"
                value={linkSearch}
                onChange={(e) => setLinkSearch(e.target.value)}
                placeholder="Search punch/observation/submittal/RFI links"
              />
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Punch Item</Label>
                <Select value={linkPunchItem || '__none__'} onValueChange={(value) => setLinkPunchItem(value === '__none__' ? '' : value)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="No punch link" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No punch link</SelectItem>
                    {punchItems.slice(0, 20).map((item) => (
                      <SelectItem key={item.id} value={item.id}>{item.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Observation</Label>
                <Select value={linkObservation || '__none__'} onValueChange={(value) => setLinkObservation(value === '__none__' ? '' : value)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="No observation link" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No observation link</SelectItem>
                    {observations.slice(0, 20).map((item) => (
                      <SelectItem key={item.id} value={item.id}>{`OBS-${item.number} ${item.title}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Submittal</Label>
                <Select value={linkSubmittal || '__none__'} onValueChange={(value) => setLinkSubmittal(value === '__none__' ? '' : value)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="No submittal link" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No submittal link</SelectItem>
                    {submittals.slice(0, 20).map((item) => (
                      <SelectItem key={item.id} value={item.id}>{`${item.submittal_number} ${item.title}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">RFI</Label>
                <Select value={linkRfi || '__none__'} onValueChange={(value) => setLinkRfi(value === '__none__' ? '' : value)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="No RFI link" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No RFI link</SelectItem>
                    {rfis.slice(0, 20).map((item) => (
                      <SelectItem key={item.id} value={item.id}>{`${item.rfi_number} ${item.title}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveLink}>Save Link</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowLinkPanel(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}
        {showActivity && (
          <Card className="absolute right-4 top-14 z-20 w-80 max-h-[70%] overflow-auto">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Recent Activity</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => setShowActivity(false)}>Close</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {isActivityLoading ? (
                <p className="text-xs text-muted-foreground">Loading activity...</p>
              ) : activityData?.results?.length ? (
                <div className="space-y-2">
                  {activityData.results.map((entry) => {
                    const actorName = entry.actor
                      ? `${entry.actor.first_name ?? ''} ${entry.actor.last_name ?? ''}`.trim() || entry.actor.email
                      : 'System';
                    return (
                      <div key={entry.id} className="rounded border p-2 text-xs">
                        <div className="font-medium">{entry.description}</div>
                        <div className="mt-1 text-muted-foreground">
                          {actorName}
                          {entry.page_number ? ` · Page ${entry.page_number}` : ''}
                          {` · ${new Date(entry.created_at).toLocaleString()}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No recent activity yet.</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      {uploadDialog}
    </div>,
    window.document.body
  );
};
